import {Button, Chip, Link, Typography} from "@mui/material";
import {
	DataTable,
	DataTableColumn,
	useDataTableStorage,
	useSpringPage,
	useSpringPageable,
	useStorage,
	View,
	ViewHeader,
} from "@variocube/app-ui";
import React, {useMemo} from "react";
import {useAsync} from "react-async-hook";
import {Link as RouterLink} from "react-router-dom";
import {CreditFilter, Creditor, useCreditorApi} from "../../../api";
import {useLocalization} from "../../../i18n";
import {CreditorFilterBar} from "./filter";

const DefaultFilter: CreditFilter = {
	companyName: "",
};

function useCreditorFilter() {
	return useStorage<CreditFilter>("CreditorFilter", DefaultFilter);
}

function useCreditorPage() {
	return useDataTableStorage("CreditorList", {
		defaults: { sortField: "company.companyName", sortDirection: "asc" },
		storageType: "session",
	});
}

export function CreditorList() {
	const {t} = useLocalization();
	const {queryCreditors} = useCreditorApi();

	const [filter, setFilter] = useCreditorFilter();
	const storage = useCreditorPage();
	const pageable = useSpringPageable(storage);

	const query = useMemo(() => ({...filter, ...pageable}), [filter, pageable]);
	const {result, loading, error} = useAsync(queryCreditors, [query]);

	const {rows, page} = useSpringPage(result);

	const columns = useMemo<DataTableColumn<Creditor>[]>(() => [
		{
			field: "company.companyName",
			label: t("creditors.singular"),
			sortable: true,
			component: ({row}) => (
				<Typography variant="body1">
					<Link component={RouterLink} to={`${row.id}`} color="secondary">
						{row.company.companyName}
					</Link>
				</Typography>
			),
		},
		{
			field: "sapCreditorNumber",
			label: t("creditors.sapCreditorNumber"),
			sortable: true,
		},
		{
			field: "checkManually",
			sortable: true,
			label: t("creditors.checkManually"),
			component: ({row}) => (
				<Chip
					color={row.checkManually ? "success" : "default"}
					label={row.checkManually ? t("common.yes") : t("common.no")}
				/>
			),
		},
		{
			field: "enabled",
			sortable: true,
			label: t("creditors.enabled"),
			component: ({row}) => (
				<Chip
					color={row.enabled ? "success" : "error"}
					label={row.enabled ? t("common.yes") : t("common.no")}
				/>
			),
		},
	], [t]);

	return (
		<View>
			<ViewHeader
				title={t("creditors.plural")}
				actions={
					<Button color="primary" variant="outlined" to="new" component={RouterLink}>
						{t("creditors.new.title")}
					</Button>
				}
			/>
			<CreditorFilterBar
				value={filter}
				onChange={setFilter}
			/>
			<DataTable
				columns={columns}
				rows={rows}
				page={page}
				loading={loading}
				error={error}
				{...storage}
			/>
		</View>
	);
}
